import React from "react";
import { useTranslation } from "react-i18next";

type Props = {
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  isEmpty: boolean;

  emptyText: string;
  loadingText?: string;

  // Optional: render the normal content when not loading/error/empty
  children: React.ReactNode;

  // Optional style hooks to match your existing CSS look
  className?: string; // wrapper class for the message line
  errorColor?: string; // default matches your other widgets
};

export default function WidgetState({
  isLoading,
  isError,
  error,
  isEmpty,
  emptyText,
  loadingText = "Loading...",
  children,
  className,
  errorColor = "#ef4130",
}: Props) {
  const { t } = useTranslation("common");
  if (isLoading) {
    return (
      <div
        className={className}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        aria-busy="true"
      >
        {loadingText}
      </div>
    );
  }

  if (isError) {
    const msg =
      error instanceof Error
        ? error.message
        : typeof error === "string"
          ? error
          : error
            ? JSON.stringify(error)
            : "Unknown error";

    return (
      <div
        className={className}
        style={{ color: errorColor }}
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        {t("errorMessage.failedLoad")}
        <div style={{ marginTop: 8, fontSize: 12, opacity: 0.85 }}>{msg}</div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div
        className={className}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {emptyText}
      </div>
    );
  }

  return <>{children}</>;
}
